import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FaDownload, FaFileInvoice, FaEye, FaUser, FaBuilding } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { formatPrice } from '../../utils/currency';

export default function SupplierInvoices() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchInvoices();
    }
  }, [user]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices/supplier-invoices?supplier_id=${user.id}`);
      setInvoices(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setLoading(false);
    }
  };

  const handleDownload = async (invoice) => {
    try {
      const response = await axios.get(
        `${API}/invoices/download/${invoice.id}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const getPaymentLabel = (method) => {
    const methods = {
      cash: i18n.language === 'fr' ? 'Espèces' : 'Cash',
      card: i18n.language === 'fr' ? 'Carte bancaire' : 'Credit Card',
      bank_transfer: i18n.language === 'fr' ? 'Virement bancaire' : 'Bank Transfer',
      purchase_order: i18n.language === 'fr' ? 'Bon de commande' : 'Purchase Order',
      delivery: i18n.language === 'fr' ? 'Paiement à la livraison' : 'Payment on Delivery'
    };
    return methods[method] || method;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540]">
            {i18n.language === 'fr' ? '📄 Factures Émises' : '📄 Issued Invoices'}
          </h1>
          
          <div className="text-sm text-gray-500">
            {invoices.length} {i18n.language === 'fr' ? 'facture(s)' : 'invoice(s)'}
          </div>
        </div>

        {invoices.length === 0 ? (
          <Card className="p-12 text-center">
            <FaFileInvoice className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {i18n.language === 'fr' 
                ? 'Aucune facture émise' 
                : 'No invoices issued'}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {i18n.language === 'fr' 
                ? 'Les factures seront générées automatiquement lors de la validation des paiements' 
                : 'Invoices will be automatically generated when payments are validated'}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {invoices.map(invoice => (
              <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-[#635BFF]/10 rounded-lg">
                          <FaFileInvoice className="text-[#635BFF]" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-[#0A2540]">
                            {invoice.invoice_number}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {invoice.description}
                          </p>
                        </div>
                      </div>
                      
                      {/* Client info */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-2 text-sm">
                          <FaUser className="text-gray-400" />
                          <span className="font-medium">{invoice.customer_name}</span>
                          {invoice.company && (
                            <>
                              <FaBuilding className="text-gray-400 ml-2" />
                              <span>{invoice.company}</span>
                            </>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{invoice.customer_email}</p>
                      </div>
                      
                      <div className="flex flex-wrap gap-3 text-sm">
                        <span className="text-gray-500">
                          📅 {new Date(invoice.created_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                        <Badge variant="outline">
                          {getPaymentLabel(invoice.payment_method)}
                        </Badge>
                        <Badge className={
                          invoice.status === 'paid' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-orange-100 text-orange-700'
                        }>
                          {invoice.status === 'paid' 
                            ? (i18n.language === 'fr' ? '✓ Payé' : '✓ Paid')
                            : (i18n.language === 'fr' ? 'En attente' : 'Pending')}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-3">
                      <p className="text-2xl font-bold text-[#0A2540]">
                        {formatPrice(invoice.amount, 'EUR')}
                      </p>
                      
                      <div className="flex gap-2">
                        <Link to={`/supplier/orders/${invoice.order_id}`}>
                          <Button variant="outline" size="sm">
                            <FaEye className="mr-2" />
                            {i18n.language === 'fr' ? 'Commande' : 'Order'}
                          </Button>
                        </Link>
                        <Button 
                          onClick={() => handleDownload(invoice)} 
                          className="bg-[#0A2540] hover:bg-[#0A2540]/90"
                        >
                          <FaDownload className="mr-2" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Summary */}
        {invoices.length > 0 && (
          <Card className="mt-8 bg-gradient-to-r from-[#635BFF] to-[#8B7FFF] text-white">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold">{invoices.length}</p>
                  <p className="text-sm opacity-80">
                    {i18n.language === 'fr' ? 'Factures émises' : 'Invoices issued'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">
                    {formatPrice(
                      invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.amount || 0), 0),
                      'EUR'
                    )}
                  </p>
                  <p className="text-sm opacity-80">
                    {i18n.language === 'fr' ? 'Encaissé' : 'Collected'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">
                    {formatPrice(
                      invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + (i.amount || 0), 0),
                      'EUR'
                    )}
                  </p>
                  <p className="text-sm opacity-80">
                    {i18n.language === 'fr' ? 'À encaisser' : 'To collect'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
